import { CommonModule } from '@angular/common';
import type { SimpleChanges, TemplateRef } from '@angular/core';
import { Component, Input, ViewChild, output, inject } from '@angular/core';

import { Ability } from '@core/abilities-types';
import type { User } from '@core/account-types';
import { trackByProp } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import type { ApprovalStepWithDecisions, ApproverDecision } from '@core/requisitions-types';
import { DecisionStatus, RequisitionFlowStepKind } from '@core/requisitions-types';
import { DateComponent } from '@core/time-ui';
import {
  AvatarComponent,
  BoxComponent,
  ButtonComponent,
  DividerComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  RtTooltip,
} from '@core/user-interface-legacy';

import { FlowStepViewContext } from '../../requisitions.types';

type ApprovalItem = {
  decisionId?: number;
  userId: number;
  user: User | null;
  statusChangedAt?: string;
  rejectionReason?: string | null;
  hasAccess: boolean;
  canRemind?: boolean;
  isRemindDisabled?: boolean;
  status: DecisionStatus;
  canBeRemoved: boolean;
};

@Component({
  selector: 'rt-requisitions-flow-step',
  templateUrl: './requisitions-flow-step.component.html',
  styleUrls: ['./requisitions-flow-step.component.less'],
  imports: [
    AvatarComponent,
    BoxComponent,
    ButtonComponent,
    CommonModule,
    DateComponent,
    DividerComponent,
    HeadingComponent,
    IconButtonComponent,
    IconComponent,
    RtTooltip,
  ],
  host: {
    '[class.context-dialog]': "this.context === 'dialog'",
  },
})
export class RequisitionsFlowStepComponent {
  private readonly i18n = inject(I18nService);

  private readonly reminderCooldown = 24 * 60 * 60 * 1000; // 24h
  public readonly FlowStepViewContext = FlowStepViewContext;

  @Input() public flowStep: ApprovalStepWithDecisions;
  @Input() public users: Record<number, User>;
  @Input() public isEditable: boolean;
  @Input() public canSendReminders: boolean;
  @Input() public context: FlowStepViewContext = FlowStepViewContext.Page;
  @Input() public isPending: boolean;

  public edit = output<void>();
  public sendReminder = output<number>();
  public removeApprover = output<number>();

  @ViewChild('statusCanceled', { static: true }) public statusCanceledTpl: TemplateRef<any>;
  @ViewChild('statusNotRequested', { static: true }) public statusNotRequestedTpl: TemplateRef<any>;
  @ViewChild('statusRequested', { static: true }) public statusRequestedTpl: TemplateRef<any>;
  @ViewChild('statusRejected', { static: true }) public statusRejectedTpl: TemplateRef<any>;
  @ViewChild('statusApproved', { static: true }) public statusApprovedTpl: TemplateRef<any>;
  @ViewChild('statusDeleted', { static: true }) public statusDeletedTpl: TemplateRef<any>;
  @ViewChild('statusNoAccess', { static: true }) public statusNoAccessTpl: TemplateRef<any>;
  @ViewChild('sendReminderTpl', { static: true }) public sendReminderTpl: TemplateRef<any>;
  @ViewChild('removeUser', { static: true }) public removeUserTpl: TemplateRef<any>;

  public stepKindLabel = '';
  public approvalItems: ApprovalItem[] = [];

  public texts = {
    deletedUser: this.i18n.translate('app.requisitions.flow.deleted_user'),
    deletedUserDescription: this.i18n.translate('app.requisitions.flow.deleted_user_description'),
    noAccessDescription: this.i18n.translate('app.requisitions.flow.no_access_description'),
    step: this.i18n.translate('app.requisitions_settings.flows.step'),
    reminderBlockedTooltip: this.i18n.translate(
      'app.requisitions.flow.send_reminder_blocked_tooltip',
    ),
    removeDeletedUserTooltip: this.i18n.translate(
      'app.requisitions.flow.remove_deleted_user_tooltip',
    ),
    sendReminder: this.i18n.translate('app.requisitions.flow.send_reminder'),
  };
  public approvalStatusTexts: Record<DecisionStatus, string> = {
    [DecisionStatus.Rejected]: this.i18n.translate('app.requisitions.approval_status.rejected'),
    [DecisionStatus.Requested]: this.i18n.translate('app.requisitions.approval_status.requested'),
    [DecisionStatus.Approved]: this.i18n.translate('app.requisitions.approval_status.approved'),
    [DecisionStatus.Canceled]: this.i18n.translate('app.requisitions.approval_status.canceled'),
    [DecisionStatus.NotRequested]: this.i18n.translate(
      'app.requisitions.approval_status.not_requested',
    ),
  };
  public trackByUserId = trackByProp<{ userId: number }>({ prop: 'userId' });

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.flowStep?.currentValue || changes.isEditable) {
      this.approvalItems = this.mapItems();
      this.setStepKindLabel();
    }
  }

  public getStatusTpl(approval: ApprovalItem): TemplateRef<any> | null {
    if (this.context === FlowStepViewContext.Dialog) {
      if (!approval.user) return this.statusDeletedTpl;
      if (!approval.hasAccess) return this.statusNoAccessTpl;
      return null;
    }

    if (approval.status === DecisionStatus.Approved) return this.statusApprovedTpl;
    if (approval.status === DecisionStatus.Rejected) return this.statusRejectedTpl;
    if (!approval.user) return this.statusDeletedTpl;
    if (!approval.hasAccess) return this.statusNoAccessTpl;

    switch (approval.status) {
      case DecisionStatus.Canceled:
        return this.statusCanceledTpl;
      case DecisionStatus.NotRequested:
        return this.statusNotRequestedTpl;
      case DecisionStatus.Requested:
        return this.statusRequestedTpl;
      default:
        return null;
    }
  }

  public getActionTpl(approval: ApprovalItem): TemplateRef<any> | null {
    if (this.context === FlowStepViewContext.Dialog) return null;
    if (!approval.user) return this.removeUserTpl;
    if (approval.canRemind) return this.sendReminderTpl;

    return null;
  }

  private setStepKindLabel() {
    this.stepKindLabel = this.i18n.translate(
      this.flowStep.kind === RequisitionFlowStepKind.All
        ? 'app.requisitions.flow.step_kind_all_label'
        : 'app.requisitions.flow.step_kind_any_label',
    );
  }

  // TODO - some of the logic below could probably be moved to *-strategy service
  private mapItems(): ApprovalItem[] {
    const decisionPerUser: Record<number, ApproverDecision> = (
      this.flowStep.approverDecisions || []
    ).reduce((acc, decision) => ({ ...acc, [decision.approverId]: decision }), {});

    const itemStatus = (id: number) => decisionPerUser[id]?.status || DecisionStatus.NotRequested;

    return this.flowStep.approverIds.map(id => {
      const decision = decisionPerUser[id];
      const status = itemStatus(id);
      const hasAccess = !!this.users[id]?.roleAbilities.includes(Ability.ViewRequisitions);
      const canBeRemoved =
        this.isEditable && !this.users[id] && this.flowStep.approverIds.length > 1;

      return {
        decisionId: decision?.id,
        userId: id,
        user: this.users[id],
        canRemind: this.canSendReminders && status === DecisionStatus.Requested && hasAccess,
        status,
        isRemindDisabled:
          status === DecisionStatus.Requested ? this.getIsRemindDisabled(decision) : true,
        statusChangedAt: decision?.submittedAt || undefined,
        rejectionReason: decision?.reason || undefined,
        hasAccess,
        canBeRemoved,
      };
    });
  }

  private getIsRemindDisabled(decision: ApproverDecision): boolean {
    if (!decision.remindedAt) return false;
    const now = new Date().valueOf();
    const lastRemindAt = new Date(decision.remindedAt).valueOf();

    return now - lastRemindAt < this.reminderCooldown;
  }
}
